/**
 * The English dictionary, and the source of truth for what keys exist.
 *
 * `as const` is what makes that true: `TranslationKey` is derived from these keys,
 * and every other language is typed as `Record<TranslationKey, string>`, so a
 * missing or misspelled translation fails `tsc` rather than falling back silently
 * at runtime in front of a user.
 *
 * Placeholders are `{name}` and are substituted by `translate()`.
 */
export const en = {
  // ─── Shared ────────────────────────────────────────────────────────────────
  'common.appName': 'Social Content Studio',
  'common.appTagline': 'Plan, create and publish across every channel',
  'common.brand': 'PostFlow',
  'common.back': 'Back',
  'common.dashboard': 'Dashboard',
  'common.previous': 'Previous',
  'common.continue': 'Continue',
  'common.loading': 'Loading…',
  'common.processing': 'Processing…',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.edit': 'Edit',
  'common.done': 'Done',
  'common.view': 'View',
  'common.prev': 'Prev',
  'common.next': 'Next',
  'common.signIn': 'Sign In',
  'common.signOut': 'Sign out',
  'common.expired': 'Expired',
  'common.emptyValue': '—',
  'common.dismiss': 'Dismiss',

  // ─── Workspaces (tenants) ──────────────────────────────────────────────────
  'workspace.label': 'Workspace',
  'workspace.members': '{count} members',
  'workspace.roleAdmin': 'Admin',
  'workspace.roleMember': 'Member',
  'workspace.create': 'Create workspace',
  'common.save': 'Save',
  'workspace.none': 'No workspace',
  'team.noWorkspace': 'You are not in a workspace yet.',
  'team.actionFailed': 'That did not work. Try again.',
  'team.invite.title': 'Invite someone',
  'team.invite.emailPlaceholder': 'name@company.com',
  'team.invite.submit': 'Invite',
  'team.invite.adminCount': '{count} of {max} admin seats used',
  'team.members.title': 'Members',
  'team.members.overrides': '{count} custom',
  'team.members.remove': 'Remove from workspace',
  'team.invitations.title': 'Pending invitations',
  'team.invitations.expires': 'Expires {date}',
  'team.invitations.revoke': 'Revoke',
  'team.teams.title': 'Teams',
  'team.teams.namePlaceholder': 'Team name',
  'team.teams.create': 'Create',
  'team.teams.delete': 'Delete team',
  'team.teams.empty': 'No teams yet.',
  'team.permissions.edit': 'Edit permissions',
  'team.permissions.help': 'Inherit follows the role. Grant and revoke override it and survive a role change.',
  'team.permissions.byRole': 'Included in this role',
  'team.permissions.notByRole': 'Not in this role',
  'team.permissions.inherit': 'Inherit',
  'team.permissions.grant': 'Grant',
  'team.permissions.revoke': 'Revoke',
  'team.permissions.saveFailed': 'Could not save those permissions.',

  // ─── Language toggle ───────────────────────────────────────────────────────
  'language.switchTo': 'Switch to {language}',

  // ─── Payment / subscription statuses ───────────────────────────────────────
  'status.pending': 'Pending',
  'status.completed': 'Completed',
  'status.failed': 'Failed',
  'status.cancelled': 'Cancelled',
  'status.active': 'Active',
  'status.expired': 'Expired',

  // ─── Platforms ─────────────────────────────────────────────────────────────
  'platform.facebook': 'Facebook',
  'platform.instagram': 'Instagram',

  // ─── Industry verticals ────────────────────────────────────────────────────
  'vertical.restaurant': 'Restaurant & café',
  'vertical.dental': 'Dental clinic',
  'vertical.real_estate': 'Real estate',
  'vertical.cleaning': 'Cleaning company',
  'vertical.agency': 'Agency',
  'vertical.other': 'Other',

  // ─── Post lifecycle ────────────────────────────────────────────────────────
  'postStatus.draft': 'Draft',
  'postStatus.pending_review': 'Awaiting review',
  'postStatus.approved': 'Approved',
  'postStatus.scheduled': 'Scheduled',
  'postStatus.publishing': 'Publishing…',
  'postStatus.published': 'Published',
  'postStatus.failed': 'Failed',
  'postStatus.cancelled': 'Cancelled',

  // ─── Login ─────────────────────────────────────────────────────────────────
  'login.title': 'Social Content Studio',
  'login.subtitle': 'AI content, scheduled and published for you',
  'login.welcome': 'Welcome back',
  'login.description': 'Sign in to manage your content and connected accounts.',
  'login.google': 'Continue with Google',
  'login.terms':
    'By signing in you agree to our Terms of Service. New users get a 14-day free trial.',
  'login.suspendedTitle': 'This account is suspended',
  'login.suspendedBody':
    'Sign-in is blocked while the suspension is in force. Contact support if you believe this is a mistake.',
  'login.failedTitle': 'Sign-in failed',

  // ─── Session expiry ────────────────────────────────────────────────────────
  'session.title': 'Your session has expired',
  'session.description':
    'You have been signed out for security. Log in again to carry on — any unsaved edits will need to be redone.',
  'session.logout': 'Log out',

  // ─── Account suspension ────────────────────────────────────────────────────
  'suspended.title': 'Your account is suspended',
  'suspended.description':
    'An administrator has suspended this account, so it cannot be used until the suspension ends. Signing in again will not restore access.',
  'suspended.descriptionIndefinite':
    'An administrator has suspended this account indefinitely. Signing in again will not restore access — contact support if you believe this is a mistake.',
  'suspended.reasonLabel': 'Reason',
  'suspended.untilLabel': 'Suspended until',
  'suspended.indefinite': 'Indefinitely',
  'suspended.logout': 'Log out',

  // ─── Dashboard navigation ──────────────────────────────────────────────────
  'nav.content': 'Content',
  'nav.setup': 'Setup',
  'nav.analytics': 'Analytics',
  'nav.account': 'Account',
  'nav.calendar': 'Calendar',
  'nav.composer': 'Composer',
  'nav.approvals': 'Approvals',
  'nav.library': 'Media library',
  'nav.brands': 'Brands',
  'nav.accounts': 'Connected accounts',
  'nav.subscription': 'Subscription',
  'nav.payments': 'Payment History',
  'nav.admin': 'Administration',
  'nav.adminUsers': 'Users',
  'nav.adminActivity': 'User Activity',
  'nav.adminStats': 'Statistics',
  'nav.posts': 'Posts',
  'nav.drafts': 'Drafts',
  'nav.inbox': 'Inbox',
  'nav.team': 'Team',
  'nav.settings': 'Settings',
  'nav.createPost': 'Create Post',
  'nav.notifications': 'Notifications',
  'nav.expand': 'Expand panel',
  'nav.collapse': 'Collapse',
  'nav.resize': 'Resize panel — use arrow keys',
  'nav.dragResize': 'Drag to resize',
  'nav.breadcrumb': 'Home / Dashboard',

  // ─── Brands ────────────────────────────────────────────────────────────────
  'brands.setupTitle': 'Set up your first brand',
  'brands.setupSubtitle':
    'Every post is written from this profile. You can add more detail anytime.',
  'brands.nameLabel': 'Business name',
  'brands.namePlaceholder': 'e.g. Sunrise Café',
  'brands.verticalLabel': 'Industry',
  'brands.timezoneLabel': 'Timezone',
  'brands.timezonePlaceholder': 'e.g. Asia/Dhaka',
  'brands.create': 'Create brand',
  'brands.creating': 'Creating…',
  'brands.createError': 'Could not create brand. Please try again.',
  'brands.limitReached': 'Your plan allows {count} brand. Upgrade to add more.',
  'brands.listTitle': 'Your brands',
  'brands.addBrand': 'Add brand',
  // ─── Connected accounts ─────────────────────────────────────────────────────
  'accounts.title': 'Connected accounts',
  'accounts.subtitle': 'Connect your social accounts to start publishing posts from the composer.',
  'accounts.connected': 'Connected',
  'accounts.notConnected': 'Not connected',
  'accounts.activeLabel': 'Active and ready to publish',
  'accounts.inactiveLabel': 'Not connected yet',
  'accounts.connect': 'Connect',
  'accounts.disconnect': 'Disconnect',
  'accounts.desc.facebook': 'Publish posts, carousels and videos to your Facebook Page.',
  'accounts.desc.instagram': 'Share photos, Reels, Stories and carousels on Instagram.',
  'accounts.noBrand': 'Create a brand first — connected accounts are filed under one.',
  'accounts.error': 'That connection did not complete. Try again.',
  'accounts.connectAnother': 'Connect another',
  'accounts.needsReauth': 'Reconnect needed',
  'accounts.picker.title': 'Choose what to connect',
  'accounts.picker.subtitle': 'These are the accounts that login can publish to.',
  'accounts.picker.confirm': 'Connect {count}',
  'accounts.picker.alreadyConnected': 'Connected',
  // ─── Media library ─────────────────────────────────────────────────────────
  'media.title': 'Media Library',
  'media.upload': 'Upload',
  'media.dropHint': 'Drop images or videos here, or click to browse',
  'media.sizeHint': 'Images up to 100 MB · Videos up to 10 GB',
  'media.uploading': 'Uploading {name}',
  'media.uploadError': 'Upload failed',
  'media.fileTooLarge': 'File exceeds the size limit',
  'media.unsupportedType': 'File type not supported',
  'media.filterAll': 'All',
  'media.filterImages': 'Images',
  'media.filterVideos': 'Videos',
  'media.filterActive': 'Active',
  'media.filterArchived': 'Archived',
  'media.filterAllStatus': 'All statuses',
  'media.searchPlaceholder': 'Search by filename…',
  'media.empty': 'No media yet',
  'media.emptyHint': 'Upload images or videos using the button above.',
  'media.emptyFilterHint': 'No files match your filters.',
  'media.archive': 'Archive',
  'media.restore': 'Restore',
  'media.delete': 'Delete permanently',
  'media.view': 'View',
  'media.archiveTitle': 'Archive this file?',
  'media.archiveBody': 'The file will be moved to the archive. You can restore it later.',
  'media.deleteTitle': 'Delete permanently?',
  'media.deleteBody': 'This cannot be undone. The file will be removed from storage.',
  'media.adminBadge': 'All creators',
  'media.ownerLabel': 'Uploaded by {email}',
  'media.dimensions': '{w} × {h}',
  'media.duration': '{s}s',
  'media.cancelUpload': 'Cancel',
  'media.dismissError': 'Dismiss',

  // ─── Not-yet-built tabs ────────────────────────────────────────────────────
  'placeholder.calendarTitle': 'Calendar is coming next',
  'placeholder.calendarBody':
    'Scheduling arrives with the publishing pipeline. Connected accounts and content generation come first.',
  'placeholder.composerTitle': 'Composer is coming next',
  'placeholder.composerBody':
    'AI generation lands in the next phase, once brand profiles exist to write from.',
  'placeholder.brandsTitle': 'Brand setup is coming next',
  'placeholder.brandsBody':
    'This is the first thing being built — the business profile every generated post is written from.',
  'placeholder.accountsTitle': 'Account connections are coming soon',
  'placeholder.accountsBody':
    'Connecting Facebook, Instagram, LinkedIn, TikTok and YouTube depends on platform review, which is already under way.',
  'placeholder.libraryTitle': 'Media library is coming soon',
  'placeholder.libraryBody': 'Image and video uploads arrive with the media pipeline.',
  'placeholder.approvalsTitle': 'Approvals are coming soon',
  'placeholder.approvalsBody':
    'Review-before-publish arrives with scheduling. It will be on by default for regulated industries.',
  'placeholder.postsTitle': 'The post list is coming next',
  'placeholder.postsBody':
    'Published and scheduled posts will be listed here. Use Create Post to open the composer.',
  'placeholder.draftsTitle': 'Drafts are coming next',
  'placeholder.draftsBody':
    'Drafts saved from the composer will be listed here once posts are stored server-side.',
  'placeholder.inboxTitle': 'Inbox is coming soon',
  'placeholder.inboxBody':
    'Comments and mentions from every connected account land here once the platform APIs are wired up.',
  'placeholder.teamTitle': 'Team management is coming soon',
  'placeholder.teamBody':
    'Invite teammates to a workspace and set their role once multi-tenant membership is built.',
  'placeholder.settingsTitle': 'Settings are coming soon',
  'placeholder.settingsBody':
    'Workspace preferences, branding and notification rules will live here.',
  'placeholder.analyticsTitle': 'Analytics are coming soon',
  'placeholder.analyticsBody':
    'Per-post performance appears once posts are publishing and there is something to measure.',

  // ─── Dashboard: subscription tab ───────────────────────────────────────────
  'subscription.signInPrompt': 'Sign in to manage your subscription.',
  'subscription.loadError': 'Could not load subscription.',
  'subscription.planLabel': '{plan} Plan',
  'subscription.daysLeft': '{days}d left',
  'subscription.daysShort': '{days}d',
  'subscription.renewsInDays': 'Renews in {days} days',
  'subscription.upgrade': 'Upgrade',
  'subscription.days': '{days} days',
  'subscription.postsUsed': 'Posts published',
  'subscription.generationsUsed': 'AI generations',
  'subscription.storageUsed': 'Media storage',
  'subscription.expiryWarning': 'Expires in {days} days. Renew soon.',
  'subscription.expiryWarningOne': 'Expires in 1 day. Renew soon.',
  'subscription.viewUpgrade': 'View / Upgrade Plans',
  'subscription.renewalTitle': 'Your subscription has expired',
  'subscription.renewalBody': 'Subscribe again to carry on publishing.',
  'subscription.renewalAction': 'View plans',
  'subscription.renewalDismiss': 'Remind me later',
  'subscription.cancelAction': 'Cancel Subscription',
  'subscription.cancelling': 'Cancelling…',
  'subscription.cancelFailed': 'Failed to cancel. Please try again.',
  'subscription.confirmTitle': 'Cancel your subscription?',
  'subscription.confirmBody':
    'You keep access until the current period ends. Scheduled posts beyond that date will not publish.',
  'subscription.confirmYes': 'Cancel subscription',
  'subscription.confirmNo': 'Keep it',
  'subscription.limitsTitle': 'Your plan limits',
  'subscription.limitBrands': 'Brands:',
  'subscription.limitAccounts': 'Connected accounts:',
  'subscription.limitHorizon': 'Schedule ahead:',
  'subscription.postsRemaining': 'Posts remaining this month:',

  // ─── Dashboard: payments tab ───────────────────────────────────────────────
  'payments.signInPrompt': 'Sign in to see your payments.',
  'payments.emptyTitle': 'No payments yet',
  'payments.emptyBody': 'Your purchases will show up here once you upgrade.',
  'payments.viewPlans': 'View Plans',

  // ─── Pricing ───────────────────────────────────────────────────────────────
  'pricing.badge': 'Subscription Plans',
  'pricing.title': 'Choose your plan',
  'pricing.subtitle':
    'AI-written content, scheduled and published across every channel you connect.',
  'pricing.perMonth': 'per month',
  'pricing.days14': '14 days',
  'pricing.contactUs': 'contact us',
  'pricing.custom': 'Custom',
  'pricing.badgeFree': '14 Days Free',
  'pricing.badgeBest': 'Best Value',
  'pricing.planFreeTrial': 'Free Trial',
  'pricing.planStarter': 'Starter',
  'pricing.planGrowth': 'Growth',
  'pricing.planAgency': 'Agency',
  'pricing.planEnterprise': 'Enterprise',
  'pricing.planLegacyProTrial': 'Pro Trial',
  'pricing.planLegacyPro': 'Pro',
  'pricing.ctaFreeTrial': 'Start Free Trial',
  'pricing.ctaStarter': 'Get Starter',
  'pricing.ctaGrowth': 'Get Growth',
  'pricing.ctaAgency': 'Get Agency',
  'pricing.ctaEnterprise': 'Contact Sales',
  'pricing.featBrands': '{count} brand',
  'pricing.featBrandsPlural': '{count} brands',
  'pricing.featAccounts': '{count} connected accounts',
  'pricing.featPosts': '{count} posts per month',
  'pricing.featGenerations': '{count} AI generations per month',
  'pricing.featStorage': '{amount} media storage',
  'pricing.featPlatforms': 'Publishes to {platforms}',
  'pricing.featScheduleHorizon': 'Schedule up to {days} days ahead',
  'pricing.featApprovals': 'Approval workflow',
  'pricing.featAnalytics': 'Post analytics',
  'pricing.featWorkspaces': 'Client workspaces',
  'pricing.featSupport': 'Dedicated support',
  'pricing.featCustomVolume': 'Custom volume',
  'pricing.featMultiUser': 'Multi-user access',
  'pricing.featBranding': 'Custom branding',
  'pricing.featInvoice': 'Invoice billing',
  'pricing.payFailed': 'Payment failed. Please try again.',

  // ─── Payment result ────────────────────────────────────────────────────────
  'payment.successTitle': 'Payment Successful!',
  'payment.successBody': 'Your subscription is now active. Start planning your content right away.',
  'payment.plan': 'Plan',
  'payment.postsPerMonth': 'Posts/month',
  'payment.expires': 'Expires',
  'payment.activating': 'Activating subscription…',
  'payment.tranId': 'Transaction ID: {id}',
  'payment.goToDashboard': 'Go to Dashboard',
  'payment.receipt': 'A receipt has been sent to your registered email address.',
  'payment.failTitle': 'Payment Failed',
  'payment.failBody': 'Your payment could not be processed. No charge was made.',
  'payment.tryAgain': 'Try Again',
  'payment.backToDashboard': 'Back to Dashboard',
  'payment.cancelTitle': 'Payment Cancelled',
  'payment.cancelBody': 'You cancelled the payment. Your current plan remains unchanged.',
  'payment.viewPlans': 'View Plans',

  // ─── Admin ─────────────────────────────────────────────────────────────────
  'admin.searchUsers': 'Search by name or email',
  'admin.colUser': 'User',
  'admin.colPlan': 'Plan',
  'admin.colSignedUp': 'Signed up',
  'admin.colLastSeen': 'Last seen',
  'admin.colGenerations': 'Generations',
  'admin.roleBadge': 'Admin',
  'admin.viewActivity': 'View activity',
  'admin.colAccount': 'Account',
  'admin.accountOk': 'Active',
  'admin.bannedBadge': 'Suspended',
  'admin.bannedUntil': 'Until {date}',
  'admin.ban': 'Suspend',
  'admin.unban': 'Lift',
  'admin.banTitle': 'Suspend this account',
  'admin.banDescription':
    '{user} will be signed out and blocked from the API and from signing in again.',
  'admin.banDuration': 'Duration',
  'admin.banDays': '{days} days',
  'admin.banIndefinite': 'Indefinite',
  'admin.banReason': 'Reason',
  'admin.banReasonPlaceholder': 'e.g. Automated abuse of the generation endpoint',
  'admin.banReasonHint': 'Shown to the user on every attempt to use the app.',
  'admin.banConfirm': 'Suspend account',
  'admin.banFailed': 'Could not suspend that account.',
  'admin.unbanFailed': 'Could not lift that suspension.',
  'admin.noUsers': 'No users match that search.',
  'admin.noActivity': 'Nothing logged yet. Activity appears here as people use the product.',
  'admin.filterAll': 'All event types',
  'admin.filterClear': 'Clear filters',
  'admin.filteredToUser': 'Showing activity for one user only.',
  'admin.showing': 'Showing {from}–{to} of {total}',
  'admin.never': 'Never',
  'admin.statTotalUsers': 'Total Users',
  'admin.statNewUsers': 'New in {days} days',
  'admin.statActiveSubs': 'Active Subscriptions',
  'admin.statGenerations': 'Content Generated',
  'admin.statPostsPublished': 'Posts Published',
  'admin.statConnectedAccounts': 'Connected Accounts',
  'admin.statNeedsReauth': 'Need Reconnecting',
  'admin.statRevenue': 'Revenue ({days}d)',
  'admin.perDayTitle': 'Generations per day',
  'admin.postsPerDayTitle': 'Posts published per day',
  'admin.planDistTitle': 'Active plans',
  'admin.platformDistTitle': 'Connected platforms',
  'admin.eventBreakdownTitle': 'Events by type',
  'admin.rangeDays': 'Last {days} days',
  'admin.noStats': 'Not enough data yet.',

  // ─── Admin: activity event labels ──────────────────────────────────────────
  'activity.user_signed_up': 'Signed up',
  'activity.user_signed_in': 'Signed in',
  'activity.content_generated': 'Generated content',
  'activity.generation_blocked': 'Generation blocked',
  'activity.generation_failed': 'Generation failed',
  'activity.brand_created': 'Created a brand',
  'activity.account_connected': 'Connected an account',
  'activity.account_disconnected': 'Disconnected an account',
  'activity.account_reauth_required': 'Account needs reconnecting',
  'activity.post_scheduled': 'Scheduled a post',
  'activity.post_published': 'Published a post',
  'activity.post_failed': 'Post failed to publish',
  'activity.post_approved': 'Approved a post',
  'activity.post_rejected': 'Rejected a post',
  'activity.token_refresh_failed': 'Token refresh failed',
  'activity.payment_initiated': 'Started a payment',
  'activity.payment_completed': 'Payment completed',
  'activity.payment_failed': 'Payment failed',
  'activity.subscription_activated': 'Plan activated',
  'activity.subscription_cancelled': 'Plan cancelled',
  'activity.user_banned': 'Account suspended',
  'activity.user_unbanned': 'Suspension lifted',
  'activity.tenant_created': 'Workspace created',
  'activity.tenant_deleted': 'Workspace deleted',
  'activity.tenant_plan_changed': 'Workspace plan changed',
  'activity.tenant_admin_changed': 'Workspace admin changed',
  'activity.member_invited': 'Invited a member',
  'activity.member_joined': 'Joined a workspace',
  'activity.member_removed': 'Removed a member',
  'activity.member_role_changed': 'Member role changed',
  'activity.permission_granted': 'Permission granted',
  'activity.permission_revoked': 'Permission revoked',
  'activity.team_created': 'Team created',
  'activity.team_deleted': 'Team deleted',
  'activity.team_membership_changed': 'Team membership changed',
  'activity.team_visibility_changed': 'Team visibility changed',
  // Retired event, retained so historical rows still render a label.
  'activity.question_generated': 'Generated a paper (legacy)',

  // ─── Composer ──────────────────────────────────────────────────────────────
  'composer.pageTitle': 'Create New Post',
  'composer.pageSubtitle': 'Publish to multiple social media platforms',
  'composer.aiAssistant': 'AI Assistant',
  'composer.aiComingSoon':
    'AI writing assistance arrives with the content generation API. Until then, write the post yourself.',
  'composer.publishOptions': 'Publishing options',
  'composer.limitsOk': 'All limits OK',
  'composer.limitsExceeded': '{count} over limit',

  // ─── Composer editor toolbar ───────────────────────────────────────────────
  'composer.tool.bold': 'Bold',
  'composer.tool.italic': 'Italic',
  'composer.tool.underline': 'Underline',
  'composer.tool.bulletList': 'Bullet list',
  'composer.tool.numberedList': 'Numbered list',
  'composer.tool.mention': 'Mention someone',
  'composer.tool.link': 'Insert link',
  'composer.tool.addImage': 'Add image',
  'composer.tool.addGif': 'Add GIF',
  'composer.tool.addVideo': 'Add video',
  'composer.tool.addLocation': 'Add location',
  'composer.emoji': 'Emoji',
  'composer.hashtag': 'Add hashtag',
  'composer.textPlaceholder': "What's on your mind? Write your post here…",
  'composer.saveDraft': 'Save draft',
  'composer.draftSaved': 'Draft saved',
  'composer.noAccounts': 'No accounts connected',
  'composer.noAccountsHint': 'Connect your social accounts to start publishing.',

  // ─── Composer sections ─────────────────────────────────────────────────────
  'composer.section.compose': 'Compose Your Post',
  'composer.section.schedule': 'Schedule',
  'composer.section.platformOptions': 'Platform settings',
  'composer.options.selectPlatformFirst': 'Select at least one platform above to see its settings.',

  // ─── Composer media library rail ───────────────────────────────────────────
  'composer.library.title': 'Media Library',
  'composer.library.search': 'Search media…',
  'composer.library.noResults': 'Nothing matches that search.',
  'composer.library.hint': 'Click a file to attach it to your post.',
  'composer.library.add': 'Attach to post',
  'composer.library.remove': 'Remove from post',
  'composer.library.collapse': 'Collapse media library',
  'composer.library.expand': 'Expand media library',
  'composer.section.preview': 'Preview',

  // ─── Composer media ────────────────────────────────────────────────────────
  'composer.media.upload': 'Upload Media',
  'composer.media.addMore': 'Add More',
  'composer.media.dropHint': 'Drag & drop media or click to upload',
  'composer.media.types': 'Supported: JPG, PNG, MP4, MOV (Max 100MB)',
  'composer.media.remove': 'Remove {name}',
  'composer.media.itemLabel': '{name} — position {position} of {total}',
  'composer.media.tooManyImages':
    'One of the selected platforms allows at most {max} images. Remove some to publish everywhere.',

  // ─── Composer schedule ─────────────────────────────────────────────────────
  'composer.schedule.now': 'Publish Now',
  'composer.schedule.nowDesc': 'Publish immediately',
  'composer.schedule.schedule': 'Schedule',
  'composer.schedule.scheduleDesc': 'Pick a date and time',
  'composer.schedule.draft': 'Save as Draft',
  'composer.schedule.draftDesc': 'Save without publishing',
  'composer.schedule.dateTime': 'Date & time',
  'composer.schedule.timezone': 'Timezone',
  'composer.schedule.willPublishAt': 'Will publish at',
  'composer.schedule.noDateSet': 'No date set',

  // ─── Composer platform options ─────────────────────────────────────────────
  'composer.options.suffix': 'options',
  'composer.options.noOptions': 'No additional options for this platform.',
  'composer.options.fb.disableComments': 'Disable comments',
  'composer.options.ig.postType': 'Post type',
  'composer.options.ig.feed': 'Feed',
  'composer.options.ig.reel': 'Reel',
  'composer.options.ig.story': 'Story',
  'composer.options.ig.firstComment': 'First comment',
  'composer.options.ig.firstCommentPlaceholder': 'Add hashtags in the first comment…',
  'composer.options.ig.location': 'Location',
  'composer.options.ig.locationPlaceholder': 'e.g. New York, NY',


  // ─── Composer preview ──────────────────────────────────────────────────────
  'composer.preview.empty': 'Select an account to see a preview',
  'composer.preview.emptyHint': 'Your post will be previewed here once you choose a platform.',
  'composer.preview.justNow': 'Just now',
  'composer.preview.noText': 'Your post text will appear here.',
  'composer.preview.comments': '{count} Comments',
  'composer.preview.shares': '{count} Shares',
  'composer.preview.like': 'Like',
  'composer.preview.comment': 'Comment',
  'composer.preview.share': 'Share',
  'composer.preview.reply': 'Reply',
  'composer.preview.repost': 'Repost',
  'composer.preview.send': 'Send',
  'composer.preview.save': 'Save',

  // ─── Composer publishing summary ───────────────────────────────────────────
  'composer.summary.title': 'Publishing Summary',
  'composer.summary.platforms': 'Platforms',
  'composer.summary.media': 'Media',
  'composer.summary.files': '{count} files',
  'composer.summary.totalChars': 'Total Characters',
  'composer.summary.estimatedTime': 'Estimated Publish Time',
  'composer.summary.minutesRange': '{min} - {max} minutes',
  'composer.summary.status': 'Status',
  'composer.summary.statusReady': 'Ready to Publish',
  'composer.summary.statusScheduled': 'Ready to Schedule',
  'composer.summary.statusDraft': 'Will Save as Draft',
  'composer.summary.statusBlocked': 'Needs Attention',
  'composer.summary.noPlatforms': 'None selected',
  'composer.summary.content': 'Content',
  'composer.summary.chars': 'chars',
  'composer.summary.image': 'image',
  'composer.summary.images': 'images',
  'composer.summary.video': 'video',
  'composer.summary.videos': 'videos',
  'composer.summary.schedule': 'Schedule',
  'composer.summary.issues': 'Issues to fix',

  // ─── Composer publish button ───────────────────────────────────────────────
  'composer.publish.now': 'Publish Now',
  'composer.publish.schedule': 'Schedule Post',
  'composer.publish.draft': 'Save as Draft',

  // ─── Composer publishing progress modal ────────────────────────────────────
  'composer.publishing.inProgress': 'Publishing your post…',
  'composer.publishing.allSuccess': 'Published successfully!',
  'composer.publishing.partialSuccess': 'Partially published',
  'composer.publishing.publishedTo': 'Published to {count} platform(s)',
  'composer.publishing.nSuccess': '{count} succeeded',
  'composer.publishing.nFailed': '{count} failed',
  'composer.publishing.retry': 'Retry',
  'composer.publishing.retryAll': 'Retry failed',
  'composer.publishing.newPost': 'New post',
  'composer.publishing.doNotClose': 'Publishing in progress — please keep this window open',
  'composer.publishing.status.queued': 'Queued…',
  'composer.publishing.status.uploading': 'Uploading media…',
  'composer.publishing.status.publishing': 'Publishing…',
  'composer.publishing.status.success': 'Published ✓',
  'composer.publishing.status.failed': 'Failed',
} as const;
